/**
 * Compact drawer data through the same GridBar + DataGrid contract as every
 * other Marble list (#1819). This adapter owns only the drawer's row content;
 * column geometry, ARIA, focus, tone and grid chrome stay in @solum/ui.
 */
import type { ReactNode } from 'react'
import { DataGrid, GridBar, type GridColumn, type GridRow } from '@solum/ui'
import s from './DrawerRows.module.css'

export interface DrawerColumn {
  key: string
  label: string
  width: string
  align?: 'right'
}

export interface DrawerRow {
  key: string
  label: string
  cells: ReactNode[]
  detail?: ReactNode
  tone?: 'warn' | 'crit' | 'muted'
  title?: string
}

export interface DrawerGridProps {
  columns: DrawerColumn[]
  rows: DrawerRow[]
  title?: ReactNode
  filters?: ReactNode
  actions?: ReactNode
  itemsLabel?: string
  empty: ReactNode
  testId: string
}

interface BoundDrawerRow extends GridRow {
  id: string
  drawer: DrawerRow
}

export function DrawerGrid({
  columns, rows, title, filters, actions, itemsLabel = 'rows', empty, testId,
}: DrawerGridProps) {
  const detailColumn = Math.min(1, Math.max(0, columns.length - 1))
  const boundRows: BoundDrawerRow[] = rows.map((drawer) => ({ id: drawer.key, drawer }))
  const gridColumns: GridColumn[] = columns.map((column, index) => ({
    key: column.key,
    label: column.label,
    width: column.width,
    align: column.align === 'right' ? 'right' : 'left',
    sortable: false,
    menuable: false,
    cell: (row) => {
      const drawer = (row as BoundDrawerRow).drawer
      const value = drawer.cells[index] ?? null
      if (index !== detailColumn || drawer.detail == null) return value
      return (
        <span className={s.stack}>
          <span className={s.value}>{value}</span>
          <span className={s.detail}>{drawer.detail}</span>
        </span>
      )
    },
  }))

  return (
    <div className={s.grid} data-testid={testId}>
      {/* The one-row bar (#2375): a drawer collection's NAME sits where a nested
          collection's view selector does, its filter in the search slot, its
          acts beside the mark. There is no title slot any more. */}
      <GridBar dense views={title} search={filters} commands={actions} testId={`${testId}-bar`} />
      <DataGrid
        columns={gridColumns}
        rows={boundRows}
        rowName={(row) => (row as BoundDrawerRow).drawer.label}
        itemsLabel={itemsLabel}
        wrapCells
        decorateRow={(row) => {
          const drawer = (row as BoundDrawerRow).drawer
          return { tone: drawer.tone, title: drawer.title }
        }}
      />
      {rows.length === 0 && <div className={s.empty}>{empty}</div>}
    </div>
  )
}
